import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import Box from '@mui/material/Box';
import CitationMarker from './CitationMarker';
import type { Citation } from './types';

interface Props {
  text: string;
  citations?: Citation[];
  /**
   * When set, only the first `maxBlocks` top-level markdown blocks are
   * rendered. Used by the collapsible assistant bubble "Show More" control.
   */
  maxBlocks?: number;
  /**
   * Render markdown images as bracketed alt text instead of an `<img>`
   * element, so the browser never fetches the URL. Default false (chat
   * bubbles are unchanged); admin views that display stored LLM output
   * enable it to block tracking-pixel/prompt-injection image fetches.
   */
  suppressRemoteImages?: boolean;
}

/**
 * Count top-level markdown blocks (paragraphs, lists, headings, code
 * blocks, tables, blockquotes, thematic breaks) in `text` by parsing to
 * MDAST and returning `root.children.length`. This matches the semantics
 * used by `remarkClampBlocks`: we slice the same array when truncating,
 * so counting and clamping agree by construction.
 */
export function countMarkdownBlocks(text: string): number {
  if (!text) return 0;
  const processor = unified().use(remarkParse).use(remarkGfm);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tree: any = processor.parse(text);
  const children = tree?.children;
  return Array.isArray(children) ? children.length : 0;
}

/**
 * Remark plugin that truncates `tree.children` to the first `maxBlocks`
 * top-level items. Runs after the citation-ref plugin so citation matching
 * still applies to the surviving blocks.
 */
function remarkClampBlocks(maxBlocks: number) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return () => (tree: any) => {
    if (
      tree?.children &&
      Array.isArray(tree.children) &&
      tree.children.length > maxBlocks
    ) {
      tree.children = tree.children.slice(0, maxBlocks);
    }
  };
}

/**
 * Inline citation regex — extends the v1 FAR-only matcher to FAR + DFARS
 * + PGI + GSAR supplement + DoD class-deviation memo identifiers. Matches:
 *   - `FAR 6.302-1(a)(2)` (historic RFO prose)
 *   - `RFO 6.302-1(a)(2)` (ordinary RFO prose)
 *   - `DFARS 206.104-70(a)`
 *   - `PGI 216.507-6(a)` / `PGI 206.103-170(d)`
 *   - `GSAR 538.7103-3(a)(2)` / `GSAR 538.7102-2(d)(2)`
 *   - `DARS 2026-O0017` / `RFO 2024-O5`
 *   - bare `538.7103-3(a)(2)` / `subpart 538.71` (no authority prefix)
 *
 * GSAR uses FAR-shaped section ids (`538.*`); upstream the supplement is
 * classified as a FAR atom (`kind: 'far'`), so a matched `GSAR <section>`
 * token maps onto the SAME `FAR <section>` lookup key its `CitationOut`
 * registers under (see `inlineMatchKeys`). This mirrors the backend's
 * generic supplement-authority alternation rather than special-casing the
 * string.
 *
 * Bare `538.x` / `subpart 538.x`: after a prompt change the model refers to
 * the FSS source using the RFO's own terminology — bare `538.7103-3(a)(2)`
 * and `subpart 538.71` with NO `GSAR`/`FAR` prefix. FAR parts only go 1..53,
 * so a `538.` section is unambiguously the FSS supplement; we route it to the
 * same `FAR 538.x` lookup key (see `inlineMatchKeys`). The bare branch is
 * scoped to the literal `538.` prefix (NOT a generic `\d+\.\d+`) so it never
 * sweeps up ordinary FAR-part-shaped numbers in prose, and it is LAST in the
 * alternation so the prefixed `GSAR 538.x` form wins when present.
 *
 * `DFARS Part 208` (part-level, no section number): a part-level DFARS
 * reference in the answer's Deviations section maps to FAR Part NN (DFARS part
 * = 200 + FAR part for parts ≤ 53; e.g. DFARS 208 ⇄ FAR 8) and links to the RFO
 * deviation guide for that part. Upstream the backend registers its
 * `CitationOut` under the canonical key `DFARS Part 2NN`, so this branch emits
 * that exact key. It is placed FIRST so the literal `Part` keyword is consumed
 * before the bare `DFARS NNN.NNN` section branch is tried.
 *
 * The Legacy FAR branch is placed FIRST so `Legacy FAR 8.405-2(d)` matches
 * here in full, consuming the leading `Legacy` keyword so the trailing
 * `FAR 8.405-2` is NOT separately re-matched by the plain-FAR branch (legacy
 * and RFO share section numbering, so the `Legacy ` token is the only
 * disambiguator — mirrors the backend's `_INLINE_CITATION_RE` in
 * `farchat/common/far_citation_v2.py`). It uses NAMED groups
 * (`legacy_sec`/`legacy_para`) so the branch is self-documenting; because JS
 * named groups still occupy positional slots, the legacy captures take slots
 * 1 & 2 and the existing numbered branches are re-indexed by +2 below.
 *
 * Group structure (mutually exclusive alternation):
 *   legacy_sec / legacy_para (slots 1-2): Legacy FAR section + paragraph
 *   3: DFARS part number (2NN)
 *   4: FAR section,    5: FAR paragraph suffix
 *   6: DFARS section,  7: DFARS paragraph suffix
 *   8: PGI section,    9: PGI paragraph suffix
 *   10: memo prefix (DARS|RFO), 11: memo number
 *   12: GSAR section, 13: GSAR paragraph suffix
 *   14: bare 538 section, 15: bare 538 paragraph suffix
 *   rfo_sec / rfo_para (slots 16-17): ordinary RFO section + paragraph
 */
const INLINE_CITATION_RE =
  /Legacy\s+FAR\s+(?<legacy_sec>\d+\.\d+(?:-\d+)?)(?<legacy_para>(?:\([a-zA-Z0-9]+\))*)|DFARS\s+Part\s+(\d{3})\b|FAR\s+(\d+\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)|DFARS\s+(\d{3}\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)|PGI\s+(\d{3}\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)|(DARS|RFO)[\s-](\d{4}-O?\d+)|GSAR\s+(\d+\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)|(?:[Ss]ubpart\s+)?(538\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)|RFO\s+(?<rfo_sec>\d+\.\d+(?:-\d+)?)(?<rfo_para>(?:\([a-zA-Z0-9]+\))*)|(?<policy>OMB\s+M-\d{2}-\d{2}|DPCAP\s+USA\d{6}-\d{2}-DPCAP)/g;

/**
 * Pre-processor that prevents the markdown parser from swallowing
 * citations into code-spans / code-blocks before the inline-citation
 * remark plugin can run.
 *
 * The synthesizer often emits citations wrapped in backticks
 * (e.g. `` `FAR 6.104` ``), which react-markdown parses as inline-code
 * — the citation visitor only walks `text` nodes so those tokens render
 * as fixed-width plain text with no link / tooltip. It can also emit a
 * citation on its own line preceded by 4+ spaces (markdown's indented
 * code-block rule). Both cases break the Detail section
 * even though Summary works because Summary is typically a single
 * paragraph that doesn't trigger either rule.
 *
 * We normalize by:
 *   1. Stripping single-backtick wrappers around bare citation tokens.
 *   2. Unwrapping whitespace-prefixed citation-only lines onto the
 *      preceding non-empty line.
 *
 * This is intentionally narrow — only citation-shaped tokens are
 * touched. Real code spans / blocks (e.g. `` `npm install` ``) are
 * unaffected.
 */
const CITATION_TOKEN_RE_SRC =
  '(?:Legacy\\s+FAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|DFARS\\s+Part\\s+\\d{3}' +
  '|FAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|RFO\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|DFARS\\s+\\d{3}\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|GSAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|(?:[Ss]ubpart\\s+)?538\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
  '|(?:DARS|RFO|PGI)[\\s-]\\d{4}-O?\\d+)';

// Match one-or-more backticks on each side (e.g. `FAR 6.104`,
// ``FAR 6.104``, etc.). The synthesizer occasionally doubles the
// backticks, which the prior single-backtick form missed.
const BACKTICKED_CITATION_RE = new RegExp(
  '`+(' + CITATION_TOKEN_RE_SRC + ')`+',
  'g'
);

const INDENTED_CITATION_LINE_RE = new RegExp(
  '(\\S[^\\n]*)\\n[ \\t]+(' + CITATION_TOKEN_RE_SRC + ')[ \\t]*(?=\\n|$)',
  'g'
);

/**
 * Strip the reference-block HEADER-LABEL prefix the synthesizer sometimes
 * echoes verbatim into prose:
 *
 *   `subpart 538.71 · 538.7103-3(a)(2)`  →  `538.7103-3(a)(2)`
 *   `subpart 537.71 · 537.7103-1(a)`     →  `537.7103-1(a)`
 *
 * The `·` separator is the tell — it is how the source corpus is
 * *labelled* for the model (`## 538.7103-3 (within subpart 538.71)`,
 * formerly `## subpart 538.71 · 538.7103-3`), NOT a citation form. When
 * echoed inside backticks the whole span (`subpart NNN.NN ·
 * NNN.NNNN-N(a)(2)`) is not a single citation token, so the
 * backtick-stripper below leaves it as a fixed-width code span with no
 * link/hover. We rewrite the stray `subpart <number> <sep>` prefix away,
 * collapsing to the trailing specific `<section>(...)` token — which the
 * backtick-stripper and the inline matcher already recognize.
 *
 * GENERALIZED: the prefix is matched by *shape*, not a hardcoded subpart.
 * It strips ANY echoed reference-block header label of the form:
 *
 *   - `subpart <number> · ` — the FSS-style `subpart NN.NN · ` label,
 *     regardless of the specific subpart number (538.71, 537.71, …), and
 *   - defensively, the more general `<label> · ` prefix the reference block
 *     uses, where `<label>` is a short run of word/number/`.`/`-`/space
 *     characters (NOT containing the `·` itself) immediately preceding a
 *     bare specific section token.
 *
 * The trailing token it collapses to must look like a real section id
 * (`<digits>.<digits>(-<digits>)?` with optional paragraph markers and an
 * optional leading `subpart `), so an ordinary sentence that merely
 * contains a `·` (e.g. `Apples · oranges and bananas.`) is NOT mangled —
 * there is no section-shaped token after the separator. A broad `subpart
 * 538.71` with no trailing specific section is likewise left untouched so
 * it still renders as its own citation.
 */
const HEADER_LABEL_PREFIX_RE = new RegExp(
  // Prefix label immediately before the separator. Two shapes, both
  // deliberately tight so the match does NOT swallow ordinary leading
  // prose words (e.g. `see …`):
  //   1. the explicit `subpart <number>` FSS label, or
  //   2. (defensively) a bare section-shaped number label (`<digits>.<digits>`
  //      with optional `-<digits>` and optional `subpart ` keyword) — the
  //      reference-block header is always number-shaped, never a plain word.
  '(?:[Ss]ubpart\\s+)?\\d+\\.\\d+(?:-\\d+)?' +
    '\\s*·\\s*' +
    // Trailing specific section token (what we collapse to). Must be a
    // section-shaped id so ordinary `· word` prose isn't matched.
    '((?:[Ss]ubpart\\s+)?\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*)',
  'g'
);

export function normalizeCitationMarkdown(text: string): string {
  if (!text) return text;
  // Drop the echoed reference-block header-label prefix BEFORE backtick
  // stripping so the cleaned `538.x(...)` token is a bare citation the
  // backtick-stripper can unwrap.
  let out = text.replace(HEADER_LABEL_PREFIX_RE, '$1');
  out = out.replace(BACKTICKED_CITATION_RE, '$1');
  // Iterate so chains of indented citation-only lines collapse onto the
  // same prose line. The regex is anchored on a preceding non-empty
  // line, so each pass joins one citation back inline.
  let prev: string;
  do {
    prev = out;
    out = out.replace(INDENTED_CITATION_LINE_RE, '$1 $2');
  } while (out !== prev);
  return out;
}

/**
 * Build the lookup key used by the remark plugin to match an inline
 * citation token against the citations array. The key is the canonical
 * token form *without* the trailing paragraph suffix:
 *
 *   - `FAR 6.302-1(a)(2)` → `FAR 6.302-1`
 *   - `DFARS 206.104-70`  → `DFARS 206.104-70`
 *   - `DARS 2026-O0017`   → `DARS 2026-O0017`
 *
 * Stripping paragraph markers lets a citation whose canonical is
 * `FAR 6.001` still match an inline mention `FAR 6.001(a)`.
 */
function citationLookupKey(canonical: string | undefined): string | null {
  if (!canonical) return null;
  // Drop trailing `(...)` paragraph markers but keep the section-level token.
  const stripped = canonical.replace(/(\([a-zA-Z0-9]+\))+\s*$/, '').trim();
  return stripped || null;
}

/**
 * Build the remark match keys from an inline regex hit, in lookup-priority
 * order. Returns up to two candidate keys so the matcher can try the
 * paragraph-form match first and fall back to the section-only form when
 * the citations list only stores the section.
 *
 * Citations lists routinely store section-level entries (e.g.
 * `DFARS 206.103-170`) while the prose mentions specific paragraphs
 * (`DFARS 206.103-170(b)`, `(c)`, `(e)`). Section-only fallback is the
 * fix that makes those prose tokens render as inline markers instead of
 * plain text.
 *
 * - **FAR**: ``[`FAR section(paragraph)`, `FAR section`]``
 * - **RFO**: ``[`FAR section(paragraph)`, `FAR section`]`` — ordinary RFO
 *   prose deliberately resolves through the unchanged canonical FAR key.
 * - **DFARS**: ``[`DFARS section(paragraph)`, `DFARS section`]``
 * - **PGI**: ``[`PGI section(paragraph)`, `PGI section`]``
 * - **DARS / RFO** (memo): ``[`DARS YYYY-OXXX`]`` only — memos have no
 *   paragraph form so there is no section-fallback to compute.
 * - **GSAR** (supplement): ``[`FAR section(paragraph)`, `FAR section`]`` —
 *   GSAR supplement sections (`538.*`) are classified upstream as FAR atoms
 *   (`kind: 'far'`), so their `CitationOut` registers under the `FAR section`
 *   key. We deliberately emit `FAR …` keys (not `GSAR …`) so the matched
 *   inline `GSAR 538.x` token resolves to that same citation; the renderer
 *   then re-labels it `GSAR` via `isGsarCitation` on the resolved citation.
 * - **bare 538** (`538.x` / `subpart 538.x`): same as GSAR — the FSS
 *   supplement `CitationOut` registers under the `FAR section` key, so a bare
 *   `538.x` token maps to ``[`FAR section(paragraph)`, `FAR section`]`` too.
 * - **Legacy FAR** (`Legacy FAR section(paragraph)`): the backend builds a
 *   `kind: 'legacy_far'` / `source: 'legacy'` `CitationOut` whose canonical is
 *   `Legacy FAR section(paragraph)`, registered under the `Legacy FAR section`
 *   key (NOT `FAR section`). We deliberately emit `Legacy FAR …` keys so the
 *   matched inline token resolves to the legacy citation and NEVER cross-links
 *   to an RFO `FAR section` of the same number — legacy/RFO disambiguate by
 *   the carried authority, not the shared section id.
 */
function inlineMatchKeys(match: RegExpExecArray): string[] {
  if (match.groups?.policy) return [match.groups.policy.replace(/\s+/g, ' ')];
  const legacySec = match.groups?.legacy_sec;
  if (legacySec) {
    // Legacy FAR (`Legacy FAR 8.405-2(d)`) → the backend registers its
    // CitationOut under the `Legacy FAR <section>` key, distinct from the RFO
    // `FAR <section>` key so a turn containing BOTH forms links each to the
    // correct citation.
    const section = `Legacy FAR ${legacySec}`;
    const para = match.groups?.legacy_para || '';
    return para ? [section + para, section] : [section];
  }
  const rfoSec = match.groups?.rfo_sec;
  if (rfoSec) {
    const section = `FAR ${rfoSec}`;
    const para = match.groups?.rfo_para || '';
    return para ? [section + para, section] : [section];
  }
  if (match[3]) {
    // Part-level DFARS (`DFARS Part 208`) → the backend registers its
    // CitationOut under the canonical `DFARS Part 2NN` key.
    return [`DFARS Part ${match[3]}`];
  }
  if (match[4]) {
    const section = `FAR ${match[4]}`;
    const para = match[5] || '';
    return para ? [section + para, section] : [section];
  }
  if (match[6]) {
    const section = `DFARS ${match[6]}`;
    const para = match[7] || '';
    return para ? [section + para, section] : [section];
  }
  if (match[8]) {
    const section = `PGI ${match[8]}`;
    const para = match[9] || '';
    return para ? [section + para, section] : [section];
  }
  if (match[10] && match[11]) {
    return [`${match[10].toUpperCase()} ${match[11]}`];
  }
  if (match[12]) {
    // GSAR → align with the FAR-keyed CitationOut (kind: 'far').
    const section = `FAR ${match[12]}`;
    const para = match[13] || '';
    return para ? [section + para, section] : [section];
  }
  if (match[14]) {
    // Bare 538.x / subpart 538.x → same FAR-keyed CitationOut as GSAR.
    const section = `FAR ${match[14]}`;
    const para = match[15] || '';
    return para ? [section + para, section] : [section];
  }
  return [];
}

/**
 * Regex used to detect citation-shaped content inside an `inlineCode`
 * node so we can demote it back to a `text` node before the citation
 * walker runs. Mirrors `CITATION_TOKEN_RE_SRC` but anchored — we only
 * unwrap code spans whose entire content is a citation token (with
 * optional surrounding whitespace), to leave real code spans alone.
 */
const INLINE_CODE_CITATION_RE = new RegExp(
  '^\\s*(?:' +
    'Legacy\\s+FAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|DFARS\\s+Part\\s+\\d{3}' +
    '|FAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|RFO\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|DFARS\\s+\\d{3}\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|PGI\\s+\\d{3}\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|GSAR\\s+\\d+\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|(?:[Ss]ubpart\\s+)?538\\.\\d+(?:-\\d+)?(?:\\([a-zA-Z0-9]+\\))*' +
    '|(?:DARS|RFO)[\\s-]\\d{4}-O?\\d+' +
    ')\\s*$'
);

/**
 * Remark plugin that converts citation-shaped `inlineCode` nodes back
 * into plain `text` nodes. Defense-in-depth alongside
 * {@link normalizeCitationMarkdown}: if the source-level backtick
 * stripper missed a token (e.g. nested formatting collapsed the
 * backtick boundary, or a future synthesizer change uses code-span
 * markup we haven't seen), this pass still gives the citation walker
 * a `text` node to find.
 */
function remarkUnwrapCitationCode() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return () => (tree: any) => {
    visit(
      tree,
      'inlineCode',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (node: any) => {
        const value: string = node.value ?? '';
        if (!INLINE_CODE_CITATION_RE.test(value)) return;
        // Mutate in place — converting `inlineCode` → `text` keeps
        // the parent's children array layout untouched.
        node.type = 'text';
        node.value = value;
      }
    );
  };
}

/**
 * General backstop (Problem B): a FAR-chat answer must NEVER render anything
 * as fixed-width font on its own line. The recurring failure mode is an
 * LLM-emitted token the citation matcher doesn't recognize getting parsed as a
 * fenced/indented code block (mdast `code`) or an own-line inline-code span
 * (mdast `inlineCode`) — which react-markdown then renders as a dead
 * monospace block/line with no hover/link.
 *
 * FAR-chat answers carry regulation prose, never source code, so it is safe to
 * suppress standalone monospace entirely here:
 *   - every `inlineCode` node is demoted to a plain `text` node, and
 *   - every block-level `code` node is demoted to a `paragraph` whose single
 *     `text` child carries the (newline-collapsed) code body.
 *
 * Because both become `text` nodes, the downstream {@link makeRemarkCitationRefs}
 * walker then runs over them exactly like ordinary prose, so any reference
 * inside (FAR x.y, subpart 538.x, `DFARS Part 208`, PGI, DARS memo, etc.)
 * still becomes a proper inline citation marker with hover/link. Legitimate
 * inline formatting (bold, lists, links) is untouched — only code/pre is
 * targeted. This plugin must run BEFORE `makeRemarkCitationRefs`.
 */
function remarkDemoteCodeToProse() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return () => (tree: any) => {
    // Inline code → text (covers own-line inline-code spans too).
    visit(
      tree,
      'inlineCode',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (node: any) => {
        node.type = 'text';
        node.value = node.value ?? '';
      }
    );
    // Block code (fenced or indented) → paragraph that PRESERVES the block's
    // line structure. We split the body on newlines and interleave mdast
    // `break` (soft line break → <br/>) nodes between the surviving text
    // lines, instead of collapsing every newline to a space. This keeps a
    // legitimately multi-line fenced block (clause text, a small aligned
    // list, etc.) from being mangled into one run-on paragraph, while still
    // rendering as ordinary prose: the node becomes a `paragraph` (not a
    // `pre`/`code`), so it is NOT fixed-width and NOT on its own monospace
    // line, and its `text` children are walked by `makeRemarkCitationRefs`
    // exactly like prose so embedded references become inline citations.
    visit(
      tree,
      'code',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (node: any) => {
        const raw: string = node.value ?? '';
        // Split into lines, trim trailing whitespace per line, and drop
        // leading/trailing blank lines so the block doesn't start/end with
        // stray <br/>s. Interior blank lines are preserved as empty text
        // nodes between breaks so vertical structure survives.
        const lines = raw.replace(/\s+$/, '').replace(/^\n+/, '').split('\n');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children: any[] = [];
        lines.forEach((line, i) => {
          if (i > 0) children.push({ type: 'break' });
          children.push({ type: 'text', value: line });
        });
        if (children.length === 0) {
          children.push({ type: 'text', value: '' });
        }
        node.type = 'paragraph';
        node.children = children;
        delete node.value;
        delete node.lang;
        delete node.meta;
      }
    );
  };
}

/**
 * Custom remark plugin that scans text nodes for inline FAR/DFARS/DARS
 * tokens and — when the token matches a citation in the supplied
 * `citationsByKey` map — rewrites the matched text as an unknown-tag
 * `citation-ref` hast node so it can be mapped to <CitationMarker /> via
 * react-markdown's `components` prop.
 *
 * The plugin is intentionally narrow: only text nodes are mutated and only
 * when the inline token appears in the lookup map. Unmatched mentions and
 * all other markdown structure — bold, lists, tables, etc. — are left
 * untouched.
 */
function makeRemarkCitationRefs(citationsByKey: Map<string, Citation>) {
  return () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (tree: any) => {
      if (citationsByKey.size === 0) return;
      visit(
        tree,
        'text',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node: any, index: number | undefined, parent: any) => {
          if (!parent || typeof index !== 'number') return;
          if (parent.type === 'citationRef') return;
          const value: string = node.value ?? '';
          // Include nodes mentioning any prefixed authority OR a bare FSS
          // supplement section. The bare form (`538.x` / `subpart 538.x`)
          // carries no authority prefix, so detect the literal `538.` id (kept
          // tight to `538.` to avoid over-scanning) and the `subpart` keyword.
          if (
            !value.includes('OMB') &&
            !value.includes('DPCAP') &&
            !value.includes('FAR') &&
            !value.includes('DFARS') &&
            !value.includes('DARS') &&
            !value.includes('PGI') &&
            !value.includes('RFO') &&
            !value.includes('GSAR') &&
            !value.includes('538.') &&
            !value.includes('subpart') &&
            !value.includes('Subpart')
          ) {
            return;
          }
          // Reset regex lastIndex for each text node (stateful `g` flag).
          INLINE_CITATION_RE.lastIndex = 0;
          let match: RegExpExecArray | null;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const children: any[] = [];
          let cursor = 0;
          let mutated = false;
          while ((match = INLINE_CITATION_RE.exec(value)) !== null) {
            const keys = inlineMatchKeys(match);
            // Try paragraph-form first, then section-only fallback. This
            // lets prose like `DFARS 206.103-170(b)` match a citations
            // list that only stores `DFARS 206.103-170` (section-only),
            // which is the common stored shape for DFARS / PGI.
            let resolvedKey: string | null = null;
            let citation: Citation | undefined;
            for (const k of keys) {
              const c = citationsByKey.get(k);
              if (c) {
                resolvedKey = k;
                citation = c;
                break;
              }
            }
            if (!resolvedKey || !citation) continue; // leave unmatched inline tokens as plain text
            mutated = true;
            if (match.index > cursor) {
              children.push({
                type: 'text',
                value: value.slice(cursor, match.index),
              });
            }
            const matchedText = match[0];
            children.push({
              type: 'citationRef',
              data: {
                hName: 'citation-ref',
                hProperties: {
                  'data-citation-key': resolvedKey,
                  'data-citation-text': matchedText,
                },
              },
              children: [],
            });
            cursor = match.index + matchedText.length;
          }
          if (!mutated) return;
          if (cursor < value.length) {
            children.push({ type: 'text', value: value.slice(cursor) });
          }
          parent.children.splice(index, 1, ...children);
          return index + children.length;
        }
      );
    };
  };
}

/**
 * Custom renderers for MUI-flavored markdown output. Kept intentionally
 * small — only the elements that need explicit styling overrides are
 * listed here; everything else falls through to the default react-markdown
 * renderers.
 */
function buildComponents(
  byKey: Map<string, Citation>,
  suppressRemoteImages = false
): Components {
  return {
    // When suppressing, markdown images render as bracketed alt text with
    // NO <img> element, so the browser never fetches the (potentially
    // attacker-controlled) URL. Used by admin views that display stored
    // LLM output outside the normal chat surface.
    ...(suppressRemoteImages
      ? {
          img: ({ alt }: { alt?: string }) => (
            <span>[image: {alt || 'image'}]</span>
          ),
        }
      : {}),
    // Map our custom `citation-ref` element to CitationMarker. The type cast
    // is needed because react-markdown's Components type only enumerates
    // standard HTML element names.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['citation-ref' as any]: ({ ...props }: any) => {
      const key =
        props['data-citation-key'] ?? props['dataCitationKey'];
      const text =
        props['data-citation-text'] ?? props['dataCitationText'] ?? '';
      const c = key ? byKey.get(String(key)) : undefined;
      if (!c) {
        return <>{text}</>;
      }
      // Wrap the marker in a span carrying ``data-citation-key`` so tests
      // (and future debug tooling) can read which citation the matcher
      // resolved a given inline token to. The wrapper is a plain inline
      // span so it does not affect rendered text or styling.
      return (
        <span data-citation-key={String(key)}>
          <CitationMarker citation={c}>{text}</CitationMarker>
        </span>
      );
    },
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    // General backstop (Problem B): never render fixed-width font on its own
    // line. FAR-chat answers are regulation prose, not source code, so any
    // `code`/`pre` that survives the `remarkDemoteCodeToProse` AST pass is
    // rendered as ordinary inline prose (no monospace, no own-line block) —
    // defense-in-depth so an unrecognized token never appears as a dead
    // fixed-width line. Inline formatting (bold/lists/links) is untouched.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    code: ({ inline, className, children, ...rest }: any) => (
      <Box component="span" {...rest}>
        {children}
      </Box>
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pre: ({ children }: any) => (
      <Box component="span">{children}</Box>
    ),
    h1: ({ children }) => (
      <Box
        component="h1"
        sx={{
          fontSize: '1.6rem',
          fontWeight: 700,
          lineHeight: 1.25,
          mt: 1.5,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    h2: ({ children }) => (
      <Box
        component="h2"
        sx={{
          fontSize: '1.35rem',
          fontWeight: 700,
          lineHeight: 1.3,
          mt: 1.5,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    h3: ({ children }) => (
      <Box
        component="h3"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          lineHeight: 1.3,
          mt: 1.5,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    h4: ({ children }) => (
      <Box
        component="h4"
        sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          lineHeight: 1.35,
          mt: 1.25,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    h5: ({ children }) => (
      <Box
        component="h5"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.4,
          mt: 1.25,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    h6: ({ children }) => (
      <Box
        component="h6"
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: 1.4,
          mt: 1.25,
          mb: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{ pl: 2.5, my: 0.5, '& li': { mb: 0.25 } }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box
        component="ol"
        sx={{ pl: 2.5, my: 0.5, '& li': { mb: 0.25 } }}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: '3px solid',
          borderColor: 'divider',
          pl: 1.25,
          my: 0.5,
          ml: 0,
          color: 'text.secondary',
          fontStyle: 'italic',
        }}
      >
        {children}
      </Box>
    ),
    hr: () => (
      <Box
        component="hr"
        sx={{
          border: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          my: 1,
        }}
      />
    ),
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 0.5 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            fontSize: '0.9em',
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1,
              py: 0.5,
              textAlign: 'left',
            },
            '& th': { bgcolor: 'action.hover', fontWeight: 600 },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    p: ({ children }) => (
      <Box component="p" sx={{ my: 0.5, '&:first-of-type': { mt: 0 }, '&:last-of-type': { mb: 0 } }}>
        {children}
      </Box>
    ),
  };
}

/**
 * Renders an assistant chat message as Markdown. Inline `RFO X.Y(...)` and
 * historic `FAR X.Y(...)` RFO tokens whose section matches an entry in
 * `citations[]` are swapped in
 * for <CitationMarker /> so the inline text itself becomes a clickable
 * link to the canonical FAR URL.
 *
 * Matching is by `citation.section` (not `citation.canonical`) so an inline
 * `RFO 6.001` or `FAR 6.001` mention matches a citation whose canonical is
 * `FAR 6.001(a)`.
 * Unmatched inline FAR mentions render as plain text.
 *
 * Security: raw HTML is intentionally NOT enabled (no `rehype-raw`), so any
 * `<script>` or similar tokens in LLM output render as escaped text.
 */
const AssistantMarkdown: React.FC<Props> = ({
  text,
  citations,
  maxBlocks,
  suppressRemoteImages,
}) => {
  const byKey = useMemo(() => {
    const m = new Map<string, Citation>();
    // First-wins on duplicate keys — matches insertion order from the
    // backend's citation_reconciler, which assigns ids in document order.
    // We register every citation under multiple keys so the inline matcher
    // can find it whether the prose says `FAR 6.001` or `FAR 6.001(a)`:
    //   - canonical-with-paragraph (e.g. `FAR 6.001(a)`)
    //   - section-only key derived from the canonical (e.g. `FAR 6.001`)
    //   - legacy v1 fallback: bare section id (`6.001`) for FAR citations
    //     whose canonical form was not populated.
    for (const c of citations ?? []) {
      const sectionOnly = citationLookupKey(c.canonical);
      if (sectionOnly && !m.has(sectionOnly)) m.set(sectionOnly, c);
      if (c.canonical && !m.has(c.canonical)) m.set(c.canonical, c);
      // v1 legacy fallback — only safe for FAR (DFARS sections collide
      // with FAR sections after the `FAR `/`DFARS ` prefix is dropped).
      if (
        c.section &&
        (c.kind === 'far' || c.kind === undefined) &&
        !m.has(`FAR ${c.section}`)
      ) {
        m.set(`FAR ${c.section}`, c);
      }
    }
    return m;
  }, [citations]);

  const components = useMemo(
    () => buildComponents(byKey, suppressRemoteImages ?? false),
    [byKey, suppressRemoteImages]
  );
  const remarkPlugins = useMemo(() => {
    const plugins: unknown[] = [
      remarkGfm,
      remarkUnwrapCitationCode(),
      remarkDemoteCodeToProse(),
      makeRemarkCitationRefs(byKey),
    ];
    if (typeof maxBlocks === 'number' && maxBlocks >= 0) {
      plugins.push(remarkClampBlocks(maxBlocks));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return plugins as any;
  }, [byKey, maxBlocks]);

  // Pre-process the synthesizer's text so citations the LLM wrapped in
  // backticks (or indented onto their own line) survive markdown parsing
  // as plain text and reach the citation-ref remark plugin. See
  // `normalizeCitationMarkdown` for the precise rules.
  const normalizedText = useMemo(() => normalizeCitationMarkdown(text), [text]);

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-of-type': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        components={components}
      >
        {normalizedText}
      </ReactMarkdown>
    </Box>
  );
};

export default React.memo(AssistantMarkdown);
